/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {startTransition, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSignals} from '@preact/signals-react/runtime';
import debounce from 'lodash.debounce';
import uniqBy from 'lodash.uniqby';
import {clearQueryParams} from '@ion/applications/src/query/state';
import {FilterTreeSearchResultBase} from '@ion/dls/src/filters/types';
import {getFilterTreeNodeKey} from '@ion/dls/src/filters/utils';
import assetLoader from '@ion/dls/src/selectiontree/utils/AssetTreeLoader';
import {TrackingMode} from '@ion/dls/src/types/types';
import {GlobalGroupsSearch} from '@ion/features/src/global-groups/search';
import {useFleetResetUpdates} from '@/routes/tracking2/hooks/useFleetResetUpdates';
import {
  resetTrackingAssetsState,
  setGroupFilters,
  setSelectedFiltersActive
} from '@/store/actions/tracking';
import {globalGroupSelector} from '@/store/selectors/global-group-chooser-selectors';
import {
  findNearbyIsActiveSelector,
  queryParamsSelector,
  searchSelectedSelector,
  trackingFiltersSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {setTrackingFilterExpandedIds, trackingFilterExpandedIds} from './store';
import {
  TrackingFavoriteFilterState,
  TrackingFilterDrawerCategoryId,
  TrackingGroupHierarchyMap,
  TrackingGroupsAssetsFilterPropsBase,
  TrackingGroupsAssetsSelectedPayload,
  TrackingGroupsAssetsTreeChange,
  TrackingGroupsAssetsTreeNode
} from './types';
import {
  areTrackingFilterSelectionsEqual,
  createEmptyTrackingGroupsAssetsPayload,
  createTrackingAssetNode,
  createTrackingAssetSearchResult,
  createTrackingGroupNode,
  createTrackingGroupsAssetsFavoriteState,
  createTrackingGroupsAssetsPayload,
  createTrackingGroupSearchResult,
  flattenTrackingGroupHierarchy,
  getTrackingAssetNodePath,
  getTrackingFirstSelectionNodePath,
  getTrackingGroupIdMap,
  getTrackingGroupsAssetsFavoritePayload,
  getTrackingGroupsAssetsPayloadFromChange,
  getTrackingGroupsAssetsSelectedCount,
  getTrackingGroupsAssetsSelectedIds,
  getTrackingRootGroup,
  getTrackingSelectedNodeId,
  getTrackingWholeGroupSelectedIds,
  indexTrackingTreeNodes,
  isViewHistoryAppliedAsset,
  normalizeTrackingAppliedAsset,
  TRACKING_GROUP_NODE_TYPE
} from './utils';

export const MIN_SEARCH_TERM_LENGTH = 3;
const MAX_SEARCH_RESULT_COUNT = 100;
const SEARCH_DEBOUNCE_MS      = 350;

interface UseTrackingGroupsAssetsSearchParams {
  groupHierarchy: TrackingGroupHierarchyMap;
  rootGroup: any;
}

// whether a group's name contains the search text, ignoring case and the
// text's surrounding spaces ...
export const isGroupNameMatch = (title: string, searchTerm: string): boolean => (
  !!title?.toLowerCase().includes(searchTerm.trim().toLowerCase())
);

/**
 * Runs the debounced cross-hierarchy groups/assets search (a Fuse.js
 * index for groups, a backend search for assets) behind the Group/Asset
 * filter's results-mode search box.
 *
 * ```typescript
 * import {useTrackingGroupsAssetsSearch} from './useTrackingGroupsAssetsFilter';
 *
 * ...
 *
 * const search = useTrackingGroupsAssetsSearch({groupHierarchy, rootGroup});
 * ```
 */
export const useTrackingGroupsAssetsSearch = ({
  groupHierarchy,
  rootGroup
}: UseTrackingGroupsAssetsSearchParams) => {
  const [searchTerm, setSearchTerm]                     = useState('');
  const [searchResults, setSearchResults]               = useState<FilterTreeSearchResultBase[]>([]);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);
  const groupsSearchRef                                 = useRef<GlobalGroupsSearch>(null);

  if (!groupsSearchRef.current) {
    groupsSearchRef.current = new GlobalGroupsSearch();
  }

  useEffect(() => {
    groupsSearchRef.current.setAccounts(rootGroup ? [rootGroup] : []);
  }, [rootGroup]);

  useEffect(() => {
    let cancelled = false;
    if (searchTerm.trim().length < MIN_SEARCH_TERM_LENGTH) {
      setSearchResults([]);
      setSearchResultsLoading(false);
      return undefined;
    }
    setSearchResultsLoading(true);
    const runSearch = async() => {
      groupsSearchRef.current.findResults(searchTerm);
      // the groups search is fuzzy (and also matches account ids), so keep
      // only the groups whose name actually contains the search text ...
      const groupResults = groupsSearchRef.current.fetchResults(1, MAX_SEARCH_RESULT_COUNT)
        .filter(({item: {title}}) => isGroupNameMatch(title, searchTerm))
        .map(({item: {title, accountId}}) => (
          createTrackingGroupSearchResult(groupHierarchy, accountId, title)
        ));
      const assetMatches = await assetLoader.requestSearch(
        searchTerm,
        getTrackingGroupIdMap(groupHierarchy),
        MAX_SEARCH_RESULT_COUNT
      );
      if (cancelled) {
        return;
      }
      const assetResults = assetMatches.map(({assetId, id: groupId, title}) => (
        createTrackingAssetSearchResult({
          assetId   : `${assetId}`,
          groupId,
          hierarchy : groupHierarchy,
          label     : title
        })
      ));
      setSearchResults([...groupResults, ...assetResults]);
      setSearchResultsLoading(false);
    };
    runSearch();
    return function() {
      cancelled = true;
    };
  }, [groupHierarchy, searchTerm]);

  // built once via useMemo (not per-render inside useCallback) so the
  // debounced wrapper's own internal timer state isn't discarded and
  // recreated on every render; cancelled on unmount so a pending
  // keystroke can't call setSearchTerm after this hook is gone...
  const onSearchTermChange = useMemo(() => debounce(function(term: string) {
    setSearchTerm(term);
  }, SEARCH_DEBOUNCE_MS), []);

  useEffect(() => function() {
    onSearchTermChange.cancel();
  }, [onSearchTermChange]);

  return {
    onSearchTermChange,
    searchResults,
    searchResultsLoading
  };
};

interface UseTrackingGlobalSearchSelectionParams {
  dispatch: ReturnType<typeof useDispatch>;
  groupHierarchy: TrackingGroupHierarchyMap;
  isGroupsAssetsExpanded: boolean;
  selectedAssets: any[];
  selectedGroups: any[];
  trackingMode: TrackingMode;
}

/**
 * Mirrors legacy's own TrackingFilters.tsx effect: applies an asset
 * selected via the top-nav global search to the Group/Asset filter's
 * selection, and resolves its tree node path for revealing/scrolling to
 * it once the Group/Asset accordion section is open.
 *
 * ```typescript
 * import {useTrackingGlobalSearchSelection} from './useTrackingGroupsAssetsFilter';
 *
 * ...
 *
 * const {pendingRevealNodePath} = useTrackingGlobalSearchSelection({
 *   dispatch,
 *   groupHierarchy,
 *   isGroupsAssetsExpanded,
 *   selectedAssets,
 *   selectedGroups,
 *   trackingMode
 * });
 * ```
 */
export const useTrackingGlobalSearchSelection = ({
  dispatch,
  groupHierarchy,
  isGroupsAssetsExpanded,
  selectedAssets,
  selectedGroups,
  trackingMode
}: UseTrackingGlobalSearchSelectionParams) => {
  const globalSearchSelected = useSelector(searchSelectedSelector);

  // read via a ref rather than added as effect deps below, so applying
  // the merged selection (which itself changes selectedAssets/
  // selectedGroups through Redux) doesn't re-trigger this same effect -
  // only a genuinely new globalSearchSelected value should...
  const appliedSelectionRef = useRef({selectedAssets, selectedGroups, trackingMode});
  appliedSelectionRef.current = {selectedAssets, selectedGroups, trackingMode};

  // resolves the globally-searched asset (if any) to both the merge-ready
  // filterAsset shape legacy dispatches with, and its tree node path - so
  // the same derivation backs both applying the selection and revealing
  // it in the tree, instead of recomputing it twice...
  const globalSearchAssetInfo = useMemo(() => {
    const searchedItem = globalSearchSelected?.item as any;
    const isSelectableAsset = globalSearchSelected?.isAsset ||
      globalSearchSelected?.isFcDevice ||
      globalSearchSelected?.isDriver;
    if (!searchedItem || !isSelectableAsset) {
      return null;
    }
    const groupId = `${searchedItem?.accountId}`;
    const assetId = `${searchedItem?.id}`;
    const name = searchedItem?.assetName || searchedItem?.deviceName;
    return {
      filterAsset : {accountId: searchedItem?.accountId, id: assetId, name},
      nodePath    : getTrackingAssetNodePath(groupHierarchy, groupId, assetId)
    };
  }, [globalSearchSelected, groupHierarchy]);

  useEffect(() => {
    // mirrors legacy's own TrackingFilters.tsx effect: selecting an asset
    // from the top-nav global search also applies it to the Group/Asset
    // filter's selection, the same way legacy's tree gets auto-checked...
    if (!globalSearchAssetInfo) {
      return;
    }
    const {
      selectedAssets : currentAssets,
      selectedGroups : currentGroups,
      trackingMode   : currentMode
    } = appliedSelectionRef.current;
    const nextSelectedAssets = uniqBy([...currentAssets, globalSearchAssetInfo.filterAsset], 'id');
    dispatch(setGroupFilters(
      createTrackingGroupsAssetsPayload(currentGroups, nextSelectedAssets),
      currentMode || TrackingMode.Live
    ) as any);
    dispatch(setSelectedFiltersActive(true) as any);
  }, [dispatch, globalSearchAssetInfo]);

  // only reveal/scroll to it once the Group/Asset accordion section is
  // actually open - reopening the section re-triggers this since the
  // value transitions from null back to the (memoized) node path...
  const pendingRevealNodePath = isGroupsAssetsExpanded
    ? globalSearchAssetInfo?.nodePath ?? null
    : null;

  return {pendingRevealNodePath};
};

/**
 * Tracks tree data, selections, and apply/clear behavior for groups/assets filters.
 *
 * ```typescript
 * import useTrackingGroupsAssetsFilter from './useTrackingGroupsAssetsFilter';
 *
 * ...
 *
 * const filter = useTrackingGroupsAssetsFilter({
 *   onSelectedCountChange : setSelectedCount
 * });
 * ```
 */
export const useTrackingGroupsAssetsFilter = ({
  onSelectedCountChange,
  onSelectionChange
}: TrackingGroupsAssetsFilterPropsBase) => {
  useSignals();
  const dispatch                      = useDispatch();
  const globalGroup                   = useSelector(globalGroupSelector);
  const trackingFilters               = useSelector(trackingFiltersSelector);
  const trackingMode                  = useSelector(trackingModeSelector);
  const findNearbyIsActive            = useSelector(findNearbyIsActiveSelector);
  const queryParams                   = useSelector(queryParamsSelector);
  const resetFleetUpdates             = useFleetResetUpdates();
  const selectedGroups                = trackingFilters?.groups?.selected ?? [];
  const appliedAssets                 = trackingFilters?.assets?.selected;
  // View History applies the asset's device record - the drawer works with
  // its own asset shape, keyed by asset id ...
  const selectedAssets                = useMemo(() => (
    (appliedAssets ?? []).map(normalizeTrackingAppliedAsset)
  ), [appliedAssets]);
  const appliedSelectedIds = useMemo(() => (
    getTrackingGroupsAssetsSelectedIds(selectedGroups, selectedAssets)
  ), [selectedAssets, selectedGroups]);
  const appliedSelectedPayload = useMemo(() => (
    createTrackingGroupsAssetsPayload(selectedGroups, selectedAssets)
  ), [selectedAssets, selectedGroups]);
  const [selectedIds, setSelectedIds] = useState<string[]>(appliedSelectedIds);
  // the tree path of a just-loaded favorite's first selection, to reveal ...
  const [favoriteRevealNodePath, setFavoriteRevealNodePath] = useState<string[] | null>(null);

  const [selectedPayload, setSelectedPayload] = useState<TrackingGroupsAssetsSelectedPayload>(
    appliedSelectedPayload
  );
  
  const selectedPayloadRef = useRef<TrackingGroupsAssetsSelectedPayload>(appliedSelectedPayload);

  const rootGroup = useMemo(() => getTrackingRootGroup(globalGroup), [globalGroup]);
  const groupHierarchy = useMemo(() => flattenTrackingGroupHierarchy(rootGroup), [rootGroup]);
  const nodes = useMemo(() => (
    rootGroup ? [createTrackingGroupNode(rootGroup)] : []
  ), [rootGroup]);

  // what each group is known to hold - its subgroups and the assets loaded
  // under it - so a group applied as a whole is ticked as a whole ...
  const loadedChildKeysRef = useRef(new Map<string, string[]>());
  const nodesByKey         = useMemo(() => indexTrackingTreeNodes(nodes), [nodes]);

  const getKnownChildKeys = useCallback((groupKey: string) => (
    loadedChildKeysRef.current.get(groupKey) ??
      (nodesByKey.get(groupKey)?.children ?? []).map(getFilterTreeNodeKey)
  ), [nodesByKey]);

  const toTreeSelectedIds = useCallback((ids: string[]) => (
    getTrackingWholeGroupSelectedIds(ids, getKnownChildKeys)
  ), [getKnownChildKeys]);

  useEffect(() => {
    assetLoader.reset();
    loadedChildKeysRef.current.clear();
  }, [globalGroup]);

  const {onSearchTermChange, searchResults, searchResultsLoading} = useTrackingGroupsAssetsSearch({
    groupHierarchy,
    rootGroup
  });

  const isGroupsAssetsExpanded = trackingFilterExpandedIds.value
    .includes(TrackingFilterDrawerCategoryId.GroupsAssets);
  const {pendingRevealNodePath: globalSearchRevealNodePath} = useTrackingGlobalSearchSelection({
    dispatch,
    groupHierarchy,
    isGroupsAssetsExpanded,
    selectedAssets,
    selectedGroups,
    trackingMode
  });

  // the asset View History applied (from the asset details, a deep link, an
  // email or a notification) is revealed - and so shown ticked - once
  // Groups/Assets opens, the same way a globally searched asset is ...
  const viewHistoryAssetNodePath = useMemo(() => {
    const viewHistoryAsset = (appliedAssets ?? []).find(isViewHistoryAppliedAsset);
    const asset            = viewHistoryAsset ? normalizeTrackingAppliedAsset(viewHistoryAsset) : null;
    return asset ? getTrackingAssetNodePath(groupHierarchy, `${asset.accountId}`, `${asset.id}`) : null;
  }, [appliedAssets, groupHierarchy]);

  const pendingRevealNodePath = globalSearchRevealNodePath ??
    (isGroupsAssetsExpanded ? (favoriteRevealNodePath ?? viewHistoryAssetNodePath) : null);

  // a newly applied View History asset opens Groups/Assets, once - so the
  // drawer shows it revealed and ticked without the section being expanded
  // by hand. Collapsing the section afterwards keeps it collapsed ...
  // keyed by the path's text, so a recomputed but identical path doesn't
  // reopen a section the user has since collapsed ...
  // a loaded favorite's selection opens it the same way ...
  const revealAssetKey = (favoriteRevealNodePath ?? viewHistoryAssetNodePath)?.join('/') ?? null;

  useEffect(() => {
    const expandedIds = trackingFilterExpandedIds.peek();
    if (revealAssetKey && !expandedIds.includes(TrackingFilterDrawerCategoryId.GroupsAssets)) {
      setTrackingFilterExpandedIds([...expandedIds, TrackingFilterDrawerCategoryId.GroupsAssets]);
    }
  }, [revealAssetKey]);

  useEffect(() => {
    setSelectedIds(toTreeSelectedIds(appliedSelectedIds));
    selectedPayloadRef.current = appliedSelectedPayload;
    setSelectedPayload(appliedSelectedPayload);
  }, [appliedSelectedIds, appliedSelectedPayload]);

  // counts what the filter applies - fully ticked groups and assets, the
  // same as the filter chips and the side nav badge - not every key the
  // tree tracks (a ticked asset's half-ticked parent groups among them) ...
  useEffect(() => {
    onSelectedCountChange?.(getTrackingGroupsAssetsSelectedCount(selectedPayload));
  }, [onSelectedCountChange, selectedPayload]);

  const loadChildren = useCallback(async(node: TrackingGroupsAssetsTreeNode) => {
    const {assets, completed} = await assetLoader.loadAssets(
      Number(node.id),
      getTrackingSelectedNodeId(TRACKING_GROUP_NODE_TYPE, node.id)
    );
    const assetNodes = assets?.map(asset => createTrackingAssetNode(asset, node.id)) ?? [];
    const children   = [
      ...(node.children ?? []),
      ...assetNodes
    ];
    loadedChildKeysRef.current.set(getFilterTreeNodeKey(node), children.map(getFilterTreeNodeKey));
    return {
      children,
      hasMore : !completed
    };
  }, []);

  const handleChange = useCallback((change: TrackingGroupsAssetsTreeChange) => {
    const payload = getTrackingGroupsAssetsPayloadFromChange(change);
    selectedPayloadRef.current = payload;
    setSelectedIds(change.selectedIds);
    startTransition(() => {
      setSelectedPayload(payload);
    });
    onSelectionChange?.();
  }, [onSelectionChange]);

  const applySelected = useCallback((value = selectedPayloadRef.current) => {
    if (findNearbyIsActive) {
      dispatch(resetTrackingAssetsState() as any);
    }
    dispatch(setGroupFilters(value, trackingMode || TrackingMode.Live) as any);
    dispatch(setSelectedFiltersActive(!!getTrackingGroupsAssetsSelectedCount(value)) as any);
    if (queryParams) {
      dispatch(clearQueryParams());
    }
    resetFleetUpdates();
  }, [
    dispatch,
    findNearbyIsActive,
    queryParams,
    resetFleetUpdates,
    trackingMode
  ]);

  const clear = useCallback(() => {
    const emptyPayload = createEmptyTrackingGroupsAssetsPayload();
    selectedPayloadRef.current = emptyPayload;
    setSelectedIds([]);
    setSelectedPayload(emptyPayload);
    onSelectionChange?.();
  }, [onSelectionChange]);

  const loadSelectedPayload = useCallback((payload: TrackingGroupsAssetsSelectedPayload) => {
    setSelectedIds(toTreeSelectedIds(getTrackingGroupsAssetsSelectedIds(
      payload.selectedGroups,
      payload.selectedAssets
    )));
    selectedPayloadRef.current = payload;
    setSelectedPayload(payload);
    onSelectionChange?.();
  }, [onSelectionChange, toTreeSelectedIds]);

  // a favorite's groups and assets are revealed in the tree - and so shown
  // ticked - the way a View History asset is, rather than left inside groups
  // still collapsed ...
  const loadFavoriteState = useCallback((state: TrackingFavoriteFilterState) => {
    const payload = getTrackingGroupsAssetsFavoritePayload(state);
    loadSelectedPayload(payload);
    setFavoriteRevealNodePath(getTrackingFirstSelectionNodePath(groupHierarchy, payload));
  }, [groupHierarchy, loadSelectedPayload]);

  const getFavoriteState = useCallback(() => (
    createTrackingGroupsAssetsFavoriteState(selectedPayloadRef.current)
  ), [selectedPayload]);

  const discard = useCallback(() => {
    selectedPayloadRef.current = appliedSelectedPayload;
    setSelectedIds(toTreeSelectedIds(appliedSelectedIds));
    setSelectedPayload(appliedSelectedPayload);
  }, [appliedSelectedIds, appliedSelectedPayload, toTreeSelectedIds]);

  // compares what applying would actually apply - the fully ticked groups
  // and assets - rather than every key the tree tracks. Revealing an asset
  // (View History, a favorite, a search) also marks its half-ticked parent
  // groups in the tree, which applies nothing new, and read as a change ...
  const hasChanges = useCallback(() => {
    const {selectedGroups, selectedAssets} = selectedPayloadRef.current ?? appliedSelectedPayload;
    return !areTrackingFilterSelectionsEqual(
      getTrackingGroupsAssetsSelectedIds(selectedGroups ?? [], selectedAssets ?? []),
      appliedSelectedIds
    );
  }, [appliedSelectedIds, appliedSelectedPayload]);

  return {
    applySelected,
    clear,
    discard,
    getFavoriteState,
    handleChange,
    hasChanges,
    loadFavoriteState,
    loadChildren,
    nodes,
    onSearchTermChange,
    pendingRevealNodePath,
    searchResults,
    searchResultsLoading,
    selectedIds
  };
};

export default useTrackingGroupsAssetsFilter;
